import React from 'react';
import { FaEdit } from 'react-icons/fa';
import { IReferralAgreementData } from './ReferralAgreementForm';
import '../Referral.css';

interface IReferralAgreementsTableProps {
  agreements: IReferralAgreementData[];
  loading: boolean;
  onCreate: () => void;
  onEntityConfig: (agreement: IReferralAgreementData) => void;
  onToggleActive: (agreement: IReferralAgreementData) => Promise<void>;
}

const REF_TYPE_LABELS: Record<string, string> = {
  GROSS_AMOUNT: 'Gross Amount',
  MSF_PERCENT_BASIS: 'MSF Basis',
  OTHER: 'Other',
};

const BRAND_COLORS = [
  '#1a1a2e', '#c8102e', '#006341', '#e94560',
  '#0f3460', '#533483', '#00704a', '#4a1942',
  '#2c3e50', '#16a085', '#8e44ad', '#1e40af',
];

function brandColor(name: string): string {
  if (!name) return '#6B7280';
  let idx = 0;
  for (let i = 0; i < name.length; i += 1) {
    idx = (idx + name.charCodeAt(i)) % BRAND_COLORS.length;
  }
  return BRAND_COLORS[idx];
}

function brandInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

const ReferralAgreementsTable: React.FC<IReferralAgreementsTableProps> = ({
  agreements,
  loading,
  onCreate,
  onEntityConfig,
  onToggleActive,
}) => {
  const defaultBips = (ag: IReferralAgreementData) => {
    if (ag.tiered) {
      const count = (ag.tiers ?? []).length;
      return `Tiered (${count} tiers)`;
    }
    return `${ag.bips ?? 0} bps`;
  };

  const refTypeClass = (refType?: string) => {
    if (refType === 'GROSS_AMOUNT') return 'ref-type-badge ref-type-badge--gross';
    if (refType === 'MSF_PERCENT_BASIS') return 'ref-type-badge ref-type-badge--msf';
    return 'ref-type-badge ref-type-badge--other';
  };

  return (
    <>
      <div className="collapsible-section-wrapper">
        <h2 className="section-title">Referral Agreements</h2>
        <button
          className="configure-button"
          onClick={onCreate}
          type="button"
        >
          + Create New
        </button>
      </div>

      {loading ? (
        <div className="ref-table-wrap">
          <div className="ref-table-head">
            <div>Brand / Connector</div>
            <div>Status</div>
            <div>Ref. Type</div>
            <div>Default BiPs</div>
            <div>Currency</div>
            <div />
          </div>
          {['a', 'b', 'c', 'd', 'e'].map((id) => (
            <div
              className="ref-table-row"
              key={`skel-${id}`}
            >
              <div className="ref-table-apm">
                <span
                  className="skel skel--circle"
                  style={{ height: 32, width: 32 }}
                />
                <span
                  className="skel"
                  style={{ height: 14, width: 130 }}
                />
              </div>
              <div>
                <span
                  className="skel skel--pill"
                  style={{ height: 22, width: 72 }}
                />
              </div>
              <div>
                <span
                  className="skel skel--pill"
                  style={{ height: 22, width: 105 }}
                />
              </div>
              <div>
                <span
                  className="skel"
                  style={{ height: 14, width: 80 }}
                />
              </div>
              <div>
                <span
                  className="skel"
                  style={{ height: 14, width: 46 }}
                />
              </div>
              <div className="ref-table-actions">
                <span
                  className="skel"
                  style={{ borderRadius: 8, height: 30, width: 110 }}
                />
                <span
                  className="skel skel--pill"
                  style={{ height: 22, width: 40 }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ref-table-wrap">
          {/* Header row */}
          <div className="ref-table-head">
            <div>Brand / Connector</div>
            <div>Status</div>
            <div>Ref. Type</div>
            <div>Default BiPs</div>
            <div>Currency</div>
            <div />
          </div>

          {agreements.length === 0 ? (
            <div className="ref-table-empty">
              No referral agreements yet. Click &quot;+ Create New&quot; to add one.
            </div>
          ) : (
            agreements.map((ag) => (
              <div
                className="ref-table-row"
                key={ag.id}
              >
                <div className="ref-table-apm">
                  <span
                    className="apm-brand-badge apm-brand-badge--sm"
                    style={{ background: brandColor(ag.targetName ?? '') }}
                  >
                    {brandInitials(ag.targetName ?? '')}
                  </span>
                  <span className="ref-table-apm-name">{ag.targetName ?? 'N/A'}</span>
                  <span
                    className="ref-type-badge ref-type-badge--other"
                    title={ag.targetType === 'CONNECTOR'
                      ? 'Covers all transactions through this connector, across all its brands'
                      : 'Covers all transactions for this brand'}
                  >
                    {ag.targetType === 'CONNECTOR' ? 'Connector' : 'Brand'}
                  </span>
                  {ag.integrationCount === 0 && (
                    <span
                      className="ref-type-badge ref-type-badge--other"
                      title="No integration yet — this agreement applies once one exists"
                    >
                      No integration yet
                    </span>
                  )}
                </div>

                <div>
                  <span className={`ref-status-badge${ag.isActive ? ' ref-status-badge--active' : ' ref-status-badge--inactive'}`}>
                    {ag.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div>
                  {!ag.refType ? (
                    <span className="ref-type-badge ref-type-badge--other">
                      <span className="ref-type-dot" />
                      Not configured
                    </span>
                  ) : (
                    <span className={refTypeClass(ag.refType)}>
                      <span className="ref-type-dot" />
                      {REF_TYPE_LABELS[ag.refType] ?? ag.refType}
                      {ag.refType === 'MSF_PERCENT_BASIS' && (
                        <span
                          className="ref-msf-warning"
                          title="MSF Basis is an estimate only — no invoice is generated"
                        >
                          ⚠
                        </span>
                      )}
                    </span>
                  )}
                </div>

                <div className="ref-table-bips">{defaultBips(ag)}</div>

                <div className="ref-table-currency">{ag.invoicingCurrency}</div>

                <div className="ref-table-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => onEntityConfig(ag)}
                    type="button"
                  >
                    <FaEdit />
                    {' '}
                    Entity Config
                  </button>
                  <label
                    className="ref-toggle"
                    htmlFor={`toggle-${ag.id}`}
                  >
                    <input
                      checked={ag.isActive}
                      id={`toggle-${ag.id}`}
                      onChange={() => onToggleActive(ag)}
                      type="checkbox"
                    />
                    <span className="ref-toggle-slider" />
                  </label>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
};

export default ReferralAgreementsTable;
